import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Mail, Link2, Trash2, Copy, Check, UserPlus, Users, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useDash } from "@/pages/dashboard/DashboardLayout";
import { Skeleton } from "@/components/ui/skeleton";

const PERMS = [
  { key: "view_revenue", label: "See revenue & analytics" },
  { key: "view_payments", label: "See payment methods" },
  { key: "manage_pnl", label: "Manage Profit & Loss" },
  { key: "delete_bookings", label: "Delete / cancel bookings" },
];

const emptyPerms = () => ({ view_revenue: false, view_payments: false, manage_pnl: false, delete_bookings: false });

function PermGrid({ value, onToggle, idPrefix }) {
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      {PERMS.map((p) => (
        <label key={p.key} className="flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm text-charcoal">
          <input type="checkbox" data-testid={`${idPrefix}-${p.key}`} checked={!!value[p.key]} onChange={() => onToggle(p.key)}
            className="h-4 w-4 accent-pulse-green" />
          {p.label}
        </label>
      ))}
    </div>
  );
}

export default function EmployeesView() {
  const { businessId } = useDash();
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [invitePerms, setInvitePerms] = useState(emptyPerms());
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["employees", businessId],
    queryFn: async () => (await api.get("/employees", { params: { business_id: businessId } })).data,
    enabled: !!businessId,
  });

  const invite = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    try {
      const res = await api.post("/invites", { email: email.trim(), permissions: invitePerms }, { params: { business_id: businessId } });
      toast.success(res.data.email_sent ? "Invite email sent" : "Invite link created");
      setEmail("");
      setInvitePerms(emptyPerms());
      qc.invalidateQueries({ queryKey: ["employees"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create invite");
    } finally { setSending(false); }
  };

  const removeInvite = async (id) => {
    await api.delete(`/invites/${id}`, { params: { business_id: businessId } });
    qc.invalidateQueries({ queryKey: ["employees"] });
    toast.success("Invite removed");
  };

  const saveEmpPerms = async (emp, key) => {
    const next = { ...emptyPerms(), ...(emp.permissions || {}), [key]: !emp.permissions?.[key] };
    try {
      await api.put(`/employees/${emp.user_id}/permissions`, { permissions: next }, { params: { business_id: businessId } });
      qc.invalidateQueries({ queryKey: ["employees"] });
      toast.success("Access updated");
    } catch (e) { toast.error("Could not update access"); }
  };

  const saveInvitePerms = async (inv, key) => {
    const next = { ...emptyPerms(), ...(inv.permissions || {}), [key]: !inv.permissions?.[key] };
    try {
      await api.put(`/invites/${inv.invite_id}/permissions`, { permissions: next }, { params: { business_id: businessId } });
      qc.invalidateQueries({ queryKey: ["employees"] });
      toast.success("Access updated");
    } catch (e) { toast.error("Could not update access"); }
  };

  const copyLink = (inv) => {
    const link = `${window.location.origin}/login?invite=${inv.token}`;
    navigator.clipboard.writeText(link);
    setCopied(inv.invite_id);
    toast.success("Invite link copied");
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-pulse-navy">Employees</h1>
        <p className="text-sm text-slate">Invite staff and decide exactly what each of them can see and do.</p>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-pulse-sm">
        <div className="flex items-center gap-2 text-sm font-semibold text-pulse-navy"><UserPlus size={18} className="text-pulse-green" /> Invite an employee</div>
        <form onSubmit={invite} className="mt-4 space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="invite-email-input"
              placeholder="employee@email.com" className="flex-1 rounded-md border border-slate-200 px-4 py-2.5 text-base outline-none focus:border-pulse-green" />
            <button type="submit" disabled={sending} data-testid="send-invite-btn"
              className="flex items-center justify-center gap-2 rounded-md bg-pulse-green px-5 py-2.5 text-sm font-semibold text-white transition-transform active:scale-[0.97] disabled:opacity-60">
              <Mail size={16} /> {sending ? "Sending…" : "Send invite"}
            </button>
          </div>
          <div>
            <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate"><ShieldCheck size={14} className="text-pulse-green" /> What can they access?</div>
            <div className="mt-2"><PermGrid value={invitePerms} onToggle={(k) => setInvitePerms((p) => ({ ...p, [k]: !p[k] }))} idPrefix="invite-perm" /></div>
            <p className="mt-2 text-xs text-slate">By default employees only see the calendar & bookings. Turn on anything extra here — you can change it anytime after they join.</p>
          </div>
        </form>
      </div>

      {isLoading ? <Skeleton className="h-40 w-full rounded-xl" /> : (
        <>
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-pulse-sm">
            <h2 className="text-sm font-semibold text-pulse-navy">Team members ({data?.employees?.length || 0})</h2>
            {data?.employees?.length ? (
              <div className="mt-4 divide-y divide-slate-100">
                {data.employees.map((emp) => (
                  <div key={emp.user_id} className="py-4" data-testid={`employee-${emp.email}`}>
                    <div className="flex items-center gap-3">
                      {emp.picture ? <img src={emp.picture} className="h-9 w-9 rounded-full object-cover" alt="" /> : <div className="grid h-9 w-9 place-items-center rounded-full bg-pulse-navy text-xs font-semibold text-white">{emp.name?.[0] || "?"}</div>}
                      <div><div className="text-sm font-medium text-charcoal">{emp.name || emp.email}</div><div className="text-xs text-slate">{emp.email}</div></div>
                    </div>
                    <div className="mt-3 pl-12">
                      <div className="mb-2 text-xs font-medium uppercase tracking-wide text-slate">Access</div>
                      <PermGrid value={emp.permissions || emptyPerms()} onToggle={(k) => saveEmpPerms(emp, k)} idPrefix={`emp-perm-${emp.email}`} />
                    </div>
                  </div>
                ))}
              </div>
            ) : <div className="mt-4 rounded-md bg-cloud py-8 text-center text-sm text-slate"><Users className="mx-auto mb-2 text-slate/50" size={28} />No team members yet</div>}
          </div>

          {data?.invites?.filter((i) => i.status === "pending").length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-pulse-sm">
              <h2 className="text-sm font-semibold text-pulse-navy">Pending invites</h2>
              <div className="mt-4 space-y-3">
                {data.invites.filter((i) => i.status === "pending").map((inv) => (
                  <div key={inv.invite_id} className="rounded-md bg-cloud p-4" data-testid={`invite-${inv.email}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2 text-sm text-charcoal"><Link2 size={15} className="text-slate" /> {inv.email}</div>
                      <div className="flex gap-1.5">
                        <button onClick={() => copyLink(inv)} data-testid={`copy-invite-${inv.email}`} className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-pulse-navy transition-colors hover:bg-cloud">
                          {copied === inv.invite_id ? <Check size={13} className="text-pulse-green" /> : <Copy size={13} />} Copy link
                        </button>
                        <button onClick={() => removeInvite(inv.invite_id)} className="grid h-8 w-8 place-items-center rounded-md text-slate transition-colors hover:bg-red-50 hover:text-red-600"><Trash2 size={14} /></button>
                      </div>
                    </div>
                    <div className="mt-3">
                      <div className="mb-2 text-xs font-medium uppercase tracking-wide text-slate">Access on join</div>
                      <PermGrid value={inv.permissions || emptyPerms()} onToggle={(k) => saveInvitePerms(inv, k)} idPrefix={`inv-perm-${inv.email}`} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
